/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';

import isEqual from 'lodash/isEqual';
import flatten from 'lodash/flatten';

import { Observable, EMPTY, of, combineLatest } from 'rxjs';

import {
  map,
  switchMap,
  mergeMap,
  withLatestFrom,
  catchError,
  concatMap,
  tap,
  filter,
} from 'rxjs/operators';

import { AuthFacade } from '@cigna/chcp/auth/data-access';

import { ManageUserAccessService } from '../manage-access.service';
import * as libActions from './manage-user-access.actions';

import {
  User,
  EditedUser,
  EntitlementRole,
  TinAccess,
  JobRole,
  UserEntitlmentResponse,
  AssignUsersIndividualRequestBody,
  AssignUsersBulkRequestBody,
  AddUsersIndividualRequestBody,
  AddUsersBulkRequestBody,
} from '../manage-access.model';

import {
  JOB_ROLE_DELEGATE_NAME,
  COLLABORATIVE_CARE_ENTITLEMENT,
  SELECTED_USER_TYPE,
  ASSIGNMENT_METHOD,
  FILTER_GROUP,
  USER_LOADING_STAGE,
} from '../manage-access.constant';

import { ServiceError, TinLob } from '@cigna/chcp/shared/util';
import { ShellTrackHandlers } from '@cigna/chcp/shared/analytics-util';

import { Router } from '@angular/router';
import { FeatureTogglesFacade } from '@cigna/features';
import differenceBy from 'lodash/differenceBy';
import { ManageUserAccessFacade } from './manage-user-access.facade';
import { LOB_DENTAL } from '@cigna/chcp/auth/util';

@Injectable()
export class ManageUserAccessEffects {
  constructor(
    private actions$: Actions,
    private manageAccesFacade: ManageUserAccessFacade,
    private authFacade: AuthFacade,
    private manageAccessService: ManageUserAccessService,
    private analytics: ShellTrackHandlers,
    private router: Router,
    private featureTogglesFacade: FeatureTogglesFacade,
  ) {}

  getAllUsers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.GET_ALL_USERS),
      map((action: libActions.GetAllUsers) => action.payload),
      withLatestFrom(this.manageAccesFacade.allUsers$),
      switchMap(([forceUpdate, allUsers]) => {
        if (allUsers.length > 0 && !forceUpdate) {
          // if we already have users we dont need to get again unless we are trying to update current list
          return of(new libActions.GetUsersCached());
        }
        return this.manageAccessService.getAllUsers().pipe(
          map((data) => new libActions.GetAllUsersSuccess(data)),
          catchError((err) => of(new libActions.GetAllUsersError(err.errMsg))),
        );
      }),
    ),
  );

  getPendingUsers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.GET_PENDING_USERS),
      switchMap(() =>
        this.manageAccessService.getPendingUsers().pipe(
          map((data) => new libActions.GetPendingUsersSuccess(data)),
          catchError((err) =>
            of(new libActions.GetPendingUsersError(err.errMsg)),
          ),
        ),
      ),
    ),
  );

  trackGetPendingUsersError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(libActions.GET_PENDING_USERS_ERROR),
        map((action: libActions.GetPendingUsersError) => action.payload),
        tap((error) => {
          this.analytics.trackServiceError(
            {},
            'chcp/delegation/v2/wam/users/pending:GET',
            error,
            'Error loading users.',
          );
        }),
      ),
    { dispatch: false },
  );

  getApprovedUsers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.GET_APPROVED_USERS),
      switchMap(() =>
        this.manageAccessService.getApprovedUsers().pipe(
          map((data) => new libActions.GetApprovedUsersSuccess(data)),
          catchError((err) => of(new libActions.GetApprovedUsersError(err))),
        ),
      ),
    ),
  );

  trackGetApprovedUsersError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(libActions.GET_APPROVED_USERS_ERROR),
        map((action: libActions.GetApprovedUsersError) => action.payload),
        tap((error) => {
          this.analytics.trackServiceError(
            {},
            'chcp/delegation/v2/wam/users/approved:GET',
            error,
            'Error loading users.',
          );
        }),
      ),
    { dispatch: false },
  );

  overviewFilterChange$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.PAGE_OVERVIEW_FILTER_GROUP_CHANGE),
      map((action: libActions.PageOverivewFilterGroupChange) => action.payload),
      filter((group) => group === FILTER_GROUP.all),
      withLatestFrom(this.manageAccesFacade.userTableView$),
      filter(([_group, stage]) => stage !== USER_LOADING_STAGE.loaded_all),
      switchMap(() => of(new libActions.GetApprovedUsers())),
    ),
  );

  getEntitlementsForSingleUser$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.VIEW_ACCESS_FOR_USER),
      map((action: libActions.ViewAccessForUser) => action.payload),
      switchMap((selectedUser) => {
        const userId = selectedUser.id;
        return this.manageAccessService.getUserTinEntitlements(userId).pipe(
          map((data) => {
            const enhancedUser = this.mapEntitlementsToUsers(
              selectedUser,
              data,
            );
            return new libActions.ViewAccessForUserSuccess(enhancedUser);
          }),
          catchError((_err) => of(new libActions.ViewAccessForUserError())),
        );
      }),
    ),
  );

  getEntitlementsForUsers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.GET_USER_TIN_ENTITLEMENTS),
      map((action: libActions.GetUserTinEntitlements) => action.payload),
      withLatestFrom(this.manageAccesFacade.selectedUsers$),
      switchMap(([_forceUpdate, selectedUsers]) => {
        const userIds = selectedUsers.map((user) => user.id).join(',');
        return this.manageAccessService.getUserTinEntitlements(userIds).pipe(
          map((data) => {
            const enhancedUsers = selectedUsers.map((user) =>
              this.mapEntitlementsToUsers(user, data),
            );
            return new libActions.GetUserTinEntitlementsSuccess(enhancedUsers);
          }),
        );
      }),
    ),
  );

  loadUsersFromDashboard$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.LOAD_USERS_FROM_DASHBOARD),
      map((action: libActions.LoadUsersFromDashboard) => action.payload),
      switchMap((userIds) =>
        this.manageAccessService.getPendingUsers().pipe(
          map(
            (data: User[]) =>
              new libActions.LoadUsersFromDashboardSuccess({
                userIds,
                users: data,
                selectedUsers: data
                  .filter((user) => userIds.includes(user.id))
                  .map((user) => ({
                    ...user,
                    accessBefore: [],
                    accessAfter: [],
                  })),
              }),
          ),
          catchError((err) =>
            of(new libActions.GetPendingUsersError(err.errMsg)),
          ),
        ),
      ),
    ),
  );

  getEntitlementsForUsersFromDashboard$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.LOAD_USERS_FROM_DASHBOARD_SUCCESS),
      map((action: libActions.LoadUsersFromDashboardSuccess) => action.payload),
      switchMap((payload) => {
        const userIds = payload.userIds.join(',');
        return this.manageAccessService.getUserTinEntitlements(userIds).pipe(
          map((data) => {
            const enhancedUsers = payload.selectedUsers.map((user) =>
              this.mapEntitlementsToUsers(user, data),
            );
            return new libActions.GetUserTinEntitlementsSuccess(enhancedUsers);
          }),
        );
      }),
    ),
  );

  getJobRoles$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.GET_JOB_ROLES),
      withLatestFrom(this.manageAccesFacade.allJobRoles$),
      switchMap(([_action, jobRoles]) => {
        if (jobRoles.length > 0) {
          return EMPTY;
        }
        return this.manageAccessService
          .getJobRoles()
          .pipe(map((data) => new libActions.GetJobRolesSuccess(data)));
      }),
    ),
  );

  getEntitlements$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.GET_ENTITLEMENTS),
      withLatestFrom(this.manageAccesFacade.allEntitlements$),
      switchMap(([_action, entitlements]) => {
        if (entitlements.length > 0) {
          return EMPTY;
        }
        return this.manageAccessService
          .getEntitlements()
          .pipe(map((data) => new libActions.GetEntitlementsSuccess(data)));
      }),
    ),
  );

  getAllTins$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.GET_ALL_TINS),
      withLatestFrom(this.manageAccesFacade.allTins$),
      switchMap(([_action, allTins]) => {
        if (allTins.length > 0) {
          return EMPTY;
        }
        return this.manageAccessService
          .getAllTins()
          .pipe(
            mergeMap((data) => [
              new libActions.GetAllTinsSuccess(data),
              new libActions.TriggerUserScaffolding(),
            ]),
          );
      }),
    ),
  );

  selectAllUsersToEdit$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.SELECT_ALL_USERS_TO_EDIT),
      withLatestFrom(this.manageAccesFacade.overviewUsers$),
      switchMap(([_action, users]) => {
        // TODO: resolve CHCP disables by bot
        // eslint-disable-next-line sonarjs/no-identical-functions
        const selectedUsers: EditedUser[] = users.map((user) => ({
          ...user,
          accessBefore: [],
          accessAfter: [],
        }));
        return of(new libActions.SetSelectedUsers(selectedUsers));
      }),
    ),
  );

  scaffoldSelectedUsers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.TRIGGER_USER_SCAFFOLDING),
      withLatestFrom(
        this.manageAccesFacade.allTins$,
        this.manageAccesFacade.selectedUserType$,
        this.manageAccesFacade.selectedUsers$,
      ),
      switchMap(([_action, allTins, selectedUserType, selectedUsers]) => {
        if (allTins) {
          const userType = selectedUserType;
          const selectedUsersBefore = selectedUsers;
          let selectedUsersAfter = [];
          if (userType === SELECTED_USER_TYPE.new) {
            selectedUsersAfter = selectedUsersBefore.map((user) =>
              this.scaffoldNewUserState(user, allTins),
            );
          } else {
            selectedUsersAfter = selectedUsersBefore.map((user) =>
              this.scaffoldExistingUserState(user, allTins),
            );
          }
          return of(new libActions.ScaffoldSelectedUsers(selectedUsersAfter));
        }
        return EMPTY;
      }),
    ),
  );

  proceedWithEditErrors$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.PROCEED_WITH_EDIT_ERRORS),
      withLatestFrom(this.manageAccesFacade.assignmentMethod$),
      switchMap(([_action, assignmentMethod]) => {
        if (assignmentMethod === ASSIGNMENT_METHOD.bulk) {
          return of(new libActions.BulkEditApplyChanges());
        }
        this.router.navigateByUrl('/my-practice/manage-access/review');

        return EMPTY;
      }),
    ),
  );

  bulkEditApplyChanges$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.BULK_EDIT_APPLY_CHANGES),
      withLatestFrom(
        this.manageAccesFacade.selectedUsers$,
        this.manageAccesFacade.bulkEditSelectedEntitlements$,
        this.manageAccesFacade.bulkEditSelectedTins$,
      ),
      switchMap(
        ([_action, selectedUsersBefore, bulkEntitlements, bulkTins]) => {
          const selectedUsersAfter = selectedUsersBefore
            // before applying bulk changes reset accessAfter in case they are coming back from review screen
            .map(
              (user: EditedUser): EditedUser => ({
                ...user,
                accessAfter: user.accessBefore,
              }),
            )
            .map((user: EditedUser) =>
              this.applyBulkAccessToUser(user, bulkTins, bulkEntitlements),
            )
            .map((data: EditedUser) =>
              this.preserveDisabledRolesForBulkEdit(data),
            );

          return of(
            new libActions.UpdateEditedUsersBulkAndContinue(selectedUsersAfter),
          );
        },
      ),
    ),
  );

  applyEntitlementsByJobRole$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.APPLY_ENTITLEMENTS_BY_JOB_ROLE),
      map((action: libActions.ApplyEntitlementsByJobRole) => action.payload),
      withLatestFrom(this.manageAccesFacade.allJobRoles$),
      switchMap(([action, jobRoles]) => {
        const user = action.user;
        const tin = action.tin;
        const jobRoleEntitlements = jobRoles.find(
          (role: JobRole) => role.id === user.jobRoleId,
        );

        return of(
          new libActions.UpdateEditedUserIndividual({
            tin,
            user,
            entitlements: jobRoleEntitlements
              ? jobRoleEntitlements.entitlements
              : [],
          }),
        );
      }),
    ),
  );

  revertEntitlementsToExisting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.REVERT_ENTITLEMENTS_TO_EXISTING),
      map((action: libActions.RevertEntitlementsToExisting) => action.payload),
      concatMap((action) => {
        const user = action.user;
        const tin = action.tin;
        const entitlementsBefore = user.accessBefore.find(
          (tinObj) => tinObj.tin === tin.tin,
        );

        return of(
          new libActions.UpdateEditedUserIndividual({
            tin,
            user,
            entitlements:
              entitlementsBefore !== undefined &&
              entitlementsBefore.entitlements
                ? entitlementsBefore.entitlements
                : [],
          }),
        );
      }),
    ),
  );

  removeAccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.USER_REMOVE_ACCESS),
      map((action: libActions.UserRemoveAccess) => action.payload),
      concatMap((payload) =>
        this.manageAccessService
          .removeAccess(payload.userId, payload.tins)
          .pipe(
            mergeMap((data) => [
              new libActions.UserRemoveAccessSuccess(data),
              new libActions.GetAllUsers(true),
            ]),
            catchError((err) => of(new libActions.UserRemoveAccessError(err))),
          ),
      ),
    ),
  );

  trackRemoveAccessError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(libActions.USER_REMOVE_ACCESS_ERROR),
        map((action: libActions.UserRemoveAccessError) => action.payload),
        tap((error) => {
          this.analytics.trackServiceError(
            {},
            'chcp/delegation/v2/user/{userId}/removeAccess:PUT',
            error,
            'Error removing access for this user.',
          );
        }),
      ),
    { dispatch: false },
  );

  resetPassword$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.USER_RESET_PASSWORD),
      map((action: libActions.UserResetPassword) => action.payload),
      concatMap((payload) =>
        this.manageAccessService.resetPassword(payload).pipe(
          map(
            (data: string) =>
              new libActions.UserResetPasswordSuccess({
                userId: payload,
                temporaryPassword: data,
              }),
          ),
          catchError((err) => of(new libActions.UserResetPasswordError(err))),
        ),
      ),
    ),
  );

  getUserTinLob$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.GET_USER_TIN_LOB),
      map((action: libActions.GetUserTinLOB) => action.userId),
      switchMap((userId) =>
        this.manageAccessService.getUserTinLob(userId).pipe(
          map((userTins) => new libActions.GetUserTinLOBSuccess(userTins)),
          catchError((err) => of(new libActions.GetUserTinLOBError(err))),
        ),
      ),
    ),
  );

  updateUserTinLob$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.UPDATE_USER_TIN_LOB),
      map((action: libActions.UpdateUserTinLOB) => action.payload),
      switchMap(({ userId, lobPref }) =>
        this.manageAccessService.updateUserTinLobPref(userId, lobPref).pipe(
          map(
            () => new libActions.UpdateUserTinLOBSuccess({ userId, lobPref }),
          ),
          catchError((err) => of(new libActions.UpdateUserTinLOBError(err))),
        ),
      ),
    ),
  );

  trackResetPasswordError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(libActions.USER_RESET_PASSWORD_ERROR),
        map((action: libActions.UserResetPasswordError) => action.payload),
        tap((error) => {
          this.analytics.trackServiceError(
            {},
            'chcp/preferences/v2/user/{userId}/resetPassword:PUT',
            error,
            'Error resetting password for this user.',
          );
        }),
      ),
    { dispatch: false },
  );

  usersSubmit$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.USERS_SUBMIT),
      withLatestFrom(
        this.manageAccesFacade.selectedUsers$,
        this.manageAccesFacade.selectedUserType$,
      ),
      concatMap(([_action, selectedUsers, selectedUserType]) => {
        if (selectedUserType === SELECTED_USER_TYPE.new) {
          return of(new libActions.AddUsersSubmit(selectedUsers));
        }

        return of(new libActions.AssignUsersSubmit(selectedUsers));
      }),
    ),
  );

  assignUsers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.ASSIGN_USERS_SUBMIT),
      map((action: libActions.AssignUsersSubmit) => action.payload),
      withLatestFrom(
        this.manageAccesFacade.assignmentMethod$,
        this.manageAccesFacade.bulkEditSelectedTins$,
        this.manageAccesFacade.bulkEditSelectedEntitlements$,
      ),
      // eslint-disable-next-line sonarjs/cognitive-complexity
      concatMap(
        ([
          payload,
          assignmentMethod,
          bulkTins,
          bulkEntitlements,
          // eslint-disable-next-line sonarjs/cognitive-complexity
        ]) => {
          const request =
            assignmentMethod === ASSIGNMENT_METHOD.bulk
              ? ({
                  users: payload.map((user) => user.id),
                  tins: bulkTins.map((tin) => ({
                    tinNumber: tin.tin,
                    lob: tin.lob,
                  })),
                  entitlementDetails: bulkEntitlements.map((entitlement) => ({
                    name: entitlement.name,
                    functions: entitlement.functions,
                  })),
                } as AssignUsersBulkRequestBody)
              : ({
                  assignAccess: flatten(
                    payload.map((user) =>
                      user.accessAfter
                        // filter out tins that didnt have any changes
                        .filter((afterTin) => {
                          const beforeTin = user.accessBefore.find(
                            (before) => before.tin === afterTin.tin,
                          );
                          if (beforeTin) {
                            return !isEqual(
                              beforeTin.entitlements,
                              afterTin.entitlements,
                            );
                          }
                          return true;
                        })
                        .map((tin) => ({
                          userId: user.id,
                          tinNumber: tin.tin,
                          lob: tin.lob,
                          entitlements: tin.entitlements
                            ? // eslint-disable-next-line sonarjs/no-identical-functions
                              tin.entitlements.map((entitlement) => {
                                if (entitlement.functions) {
                                  return {
                                    name: entitlement.name,
                                    functions: entitlement.functions,
                                  };
                                }
                                return { name: entitlement.name };
                              })
                            : [],
                        })),
                    ),
                  ),
                } as AssignUsersIndividualRequestBody);

          return this.manageAccessService
            .assignAccess(request, assignmentMethod)
            .pipe(
              mergeMap((data) => [
                new libActions.AssignUsersSuccess(data),
                new libActions.GetAllUsers(true),
              ]),
              catchError((err: ServiceError) => {
                if (
                  err.errObject.status === 412 &&
                  err.errObject.additionalDetails
                ) {
                  return of(
                    new libActions.ShowEditErrors({
                      error: err,
                      overDelegateLimit:
                        err.errObject.additionalDetails[0].overDelegateLimit ||
                        [],
                      overTinLimit:
                        err.errObject.additionalDetails[0].overTinLimit || [],
                      lastAMForTin:
                        err.errObject.additionalDetails[0].lastAMForTin || [],
                    }),
                  );
                }
                if (
                  err.errObject.status === 400 &&
                  err.errObject.additionalDetails &&
                  err.errObject.additionalDetails[0].message
                    .toLowerCase()
                    .includes('limit of 15 website access managers')
                ) {
                  err.errMsg = err.errObject.additionalDetails[0].message;
                }
                return of(new libActions.AssignUsersError(err));
              }),
            );
        },
      ),
    ),
  );

  addUsers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.ADD_USERS_SUBMIT),
      map((action: libActions.AddUsersSubmit) => action.payload),
      withLatestFrom(
        this.authFacade.lob$,
        this.manageAccesFacade.assignmentMethod$,
        this.manageAccesFacade.bulkEditSelectedTins$,
        this.manageAccesFacade.bulkEditSelectedEntitlements$,
      ),
      concatMap(
        // eslint-disable-next-line sonarjs/cognitive-complexity
        ([
          payload,
          lobType,
          assignmentMethod,
          bulkTins,
          bulkEntitlements,
          // eslint-disable-next-line sonarjs/cognitive-complexity
        ]) => {
          const request =
            assignmentMethod === ASSIGNMENT_METHOD.bulk
              ? ({
                  // bulk user/tin assignment
                  users: payload.map((user: EditedUser) => ({
                    firstName: user.firstName,
                    lastName: user.lastName,
                    emailAddress: user.email,
                    phoneNumber: user.phone || undefined,
                    phoneExt: user.phoneExt || undefined,
                    jobRoleId: user.jobRoleId
                      ? user.jobRoleId.toString()
                      : null, // api expects string for this, would be great to keep as number all the way through
                  })),
                  tins: bulkTins.map((tin) => ({
                    tinNumber: tin.tin,
                    lob: tin.lob,
                  })),
                  entitlementDetails: bulkEntitlements.map((entitlement) => ({
                    name: entitlement.name,
                    functions: entitlement.functions,
                  })),
                } as AddUsersBulkRequestBody)
              : ({
                  // individual user/tin assignment
                  users: payload.map((user: EditedUser) => ({
                    firstName: user.firstName,
                    lastName: user.lastName,
                    emailAddress: user.email,
                    phoneNumber: user.phone || undefined,
                    phoneExt: user.phoneExt || undefined,
                    jobRoleId: user.jobRoleId
                      ? user.jobRoleId.toString()
                      : null, // api expects string for this, would be great to keep as number all the way through
                    tins: user.accessAfter
                      ? user.accessAfter
                          .filter(
                            (tin) =>
                              tin.entitlements && tin.entitlements.length > 0,
                          )
                          .map((tin) => ({
                            tinNumber: tin.tin,
                            lob: tin.lob,
                            entitlements: tin.entitlements
                              ? tin.entitlements.map(
                                  (entitlement) => entitlement.name,
                                )
                              : [],
                            // TODO: why are we passing entitlementDetails here but not for assign access when we just use entitlments?
                            entitlementDetails: tin.entitlements
                              ? // eslint-disable-next-line sonarjs/no-identical-functions
                                tin.entitlements.map((entitlement) => {
                                  if (entitlement.functions) {
                                    return {
                                      name: entitlement.name,
                                      functions: entitlement.functions,
                                    };
                                  }
                                  return { name: entitlement.name };
                                })
                              : [],
                          }))
                      : [],
                  })),
                } as AddUsersIndividualRequestBody);

          if (lobType) {
            return this.manageAccessService
              .addUsers(request, lobType, assignmentMethod)
              .pipe(
                mergeMap((data) => [
                  new libActions.AddUsersSuccess(data),
                  new libActions.GetAllUsers(true),
                ]),
                catchError((err) => {
                  if (
                    err.errObject.status === 400 &&
                    err.errObject.additionalDetails &&
                    err.errObject.additionalDetails[0].message
                      .toLowerCase()
                      .includes('limit of 15 website access managers')
                  ) {
                    err.errMsg = err.errObject.additionalDetails[0].message;
                  }
                  return of(new libActions.AddUsersError(err));
                }),
              );
          }

          return of(new libActions.AddUsersNoLobError());
        },
      ),
    ),
  );

  addUsersSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(libActions.ADD_USERS_SUCCESS),
      map((action: libActions.AddUsersSuccess) => action.payload),
      withLatestFrom(this.manageAccesFacade.selectedUsers$),
      switchMap(([payload, selectedUsers]) => {
        const selectedUsersMerged = selectedUsers.map((user) => {
          const matchedUser = payload.find(
            (responseUser) =>
              responseUser.firstName.toLowerCase() ===
                user.firstName.toLowerCase() &&
              responseUser.lastName.toLowerCase() ===
                user.lastName.toLowerCase() &&
              responseUser.email.toLowerCase() === user.email.toLowerCase(),
          );
          return matchedUser
            ? {
                ...user,
                jobRole: matchedUser.jobRole,
                lob: matchedUser.lob,
                lobPref: matchedUser.lobPref,
                temporaryUserName: matchedUser.temporaryUserName,
                temporaryPassword: matchedUser.temporaryPassword,
              }
            : user;
        });

        return of(new libActions.AddUsersSuccessMerge(selectedUsersMerged));
      }),
    ),
  );

  trackAddUsersError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(libActions.ADD_USERS_ERROR),
        map((action: libActions.AddUsersError) => action.payload),
        tap((error) => {
          this.analytics.trackServiceError(
            {},
            'chcp/v2/registration/lobType/{lobType}/addUsers:POST',
            error,
            'Error adding new users',
          );
        }),
      ),
    { dispatch: false },
  );

  trackAssignUsersError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(libActions.ASSIGN_USERS_ERROR),
        map((action: libActions.AssignUsersError) => action.payload),
        tap((error) => {
          this.analytics.trackServiceError(
            {},
            'chcp/delegation/v2/assignAccess:PUT',
            error,
            'Error udpating user access',
          );
        }),
      ),
    { dispatch: false },
  );

  trackShowEditErrors$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(libActions.SHOW_EDIT_ERRORS),
        map((action: libActions.ShowEditErrors) => action.payload),
        tap((payload) => {
          this.analytics.trackServiceError(
            {},
            'chcp/delegation/v2/assignAccess:PUT',
            payload.error,
            'Error udpating user access',
          );
        }),
      ),
    { dispatch: false },
  );

  /* ROUTING ONLY EFFECTS, REPLACES PAGE_SCREEN STATE FROM OLD VERSION */

  goToAssignMethodPage$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          libActions.ADD_USERS_AND_START_ASSIGNMENT,
          libActions.VIEW_AND_EDIT_USERS,
          libActions.SELECT_AND_EDIT_USER,
        ),
        tap(() => {
          this.router.navigateByUrl(
            '/my-practice/manage-access/assignment-method',
          );
        }),
      ),
    { dispatch: false },
  );

  goToAssignAccessPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          libActions.SET_ASSIGNMENT_METHOD_AND_ASSIGN,
          libActions.BACK_TO_ASSIGN_ACCESS,
        ),
        withLatestFrom(this.manageAccesFacade.assignmentMethod$),
        tap(([_payload, type]) => {
          if (type === ASSIGNMENT_METHOD.individual) {
            this.router.navigateByUrl(
              '/my-practice/manage-access/access-individual',
            );
          } else {
            this.router.navigateByUrl('/my-practice/manage-access/access-bulk');
          }
        }),
      ),
    { dispatch: false },
  );

  goToReviewPage$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(libActions.UPDATE_EDITED_USERS_BULK_AND_CONTINUE),
        tap(() => {
          this.router.navigateByUrl('/my-practice/manage-access/review');
        }),
      ),
    { dispatch: false },
  );

  goToAddEntitlementSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(libActions.ADD_USERS_SUCCESS),
        withLatestFrom(this.authFacade.lob$),
        tap(([_access, lob]) => {
          this.router.navigateByUrl(
            lob !== LOB_DENTAL
              ? '/my-practice/manage-access/lob-pref'
              : '/my-practice/manage-access/success',
          );
        }),
        tap(() => {
          // had to add in existing effect to make sure it fires after route change
          this.analytics.trackEventOther(
            {},
            'user add completion',
            'ac-WC111-add completion',
            { events: 'event442' },
          );
        }),
      ),
    { dispatch: false },
  );

  goToSuccessAssign$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(libActions.ASSIGN_USERS_SUCCESS),
        // eslint-disable-next-line sonarjs/no-identical-functions
        tap((_access) => {
          this.router.navigateByUrl('/my-practice/manage-access/success');
        }),
        tap(() => {
          // had to add in existing effect to make sure it fires after route change
          this.analytics.trackEventOther(
            {},
            'user edit completion',
            'ac-WC113-edit completion',
            { events: 'event444' },
          );
        }),
      ),
    { dispatch: false },
  );

  trackStartEditUser$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(libActions.SET_ASSIGNMENT_METHOD_AND_ASSIGN),
        withLatestFrom(this.manageAccesFacade.selectedUserType$),
        switchMap(([, type]) => {
          if (type === 'existing') {
            this.analytics.trackEventOther({}, 'Next', 'ac-WC13A-Edit Start', {
              events: 'event443',
            });
          }
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  trackStartAddUsers$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(libActions.ADD_USERS_AND_START_ASSIGNMENT),
        tap(() => {
          this.analytics.trackEventOther(
            {},
            'Assign Access',
            'ac-WC11A-Add Start',
            { events: 'event441' },
          );
        }),
      ),
    { dispatch: false },
  );

  mapEntitlementsToUsers = (
    user: User,
    entitlementArray: UserEntitlmentResponse[],
  ): User => {
    const updatedUser: UserEntitlmentResponse | undefined =
      entitlementArray.find((usr) => usr.id === user.id);

    if (updatedUser !== undefined) {
      return {
        ...user,
        tins: user.tins.map((tin) => {
          const matchedTin = updatedUser.tins.find((t) => t.tin === tin.tin);

          if (matchedTin !== undefined && matchedTin.entitlements) {
            return {
              ...tin,
              entitlements: matchedTin.entitlements,
            };
          }
          return {
            ...tin,
            entitlements: [],
          };
        }),
      };
    }

    // safety valve in case no user found, just set entitlments to empty array
    return {
      ...user,
      tins: user.tins.map((tin) => ({
        ...tin,
        entitlements: [],
      })),
    };
  };

  scaffoldNewUserState = (user: User, allTins: TinAccess[]): EditedUser => ({
    ...user,
    accessBefore: allTins.map((tin) => ({
      ...tin,
      entitlements: [],
    })),
    accessAfter: allTins.map((tin) => ({
      ...tin,
      entitlements: [],
    })),
  });

  scaffoldExistingUserState = (
    user: User,
    allTins: TinAccess[],
  ): EditedUser => {
    const missingTins = this.addTinsIfNeeded(user.tins, allTins);
    return {
      ...user,
      accessBefore: user.tins.concat(missingTins),
      accessAfter: user.tins.concat(missingTins),
    };
  };

  addTinsIfNeeded = (
    existingTins: TinAccess[],
    allTins: TinAccess[],
  ): TinAccess[] =>
    allTins
      .filter(
        (tin) =>
          existingTins.findIndex(
            (existingTin) => existingTin.tin === tin.tin,
          ) === -1,
      )
      .map((tin) => ({
        ...tin,
        entitlements: [],
      }));

  applyBulkAccessToUser = (
    user: EditedUser,
    modifiedTins: TinLob[],
    selectedEntitlements: EntitlementRole[],
  ): EditedUser => ({
    ...user,
    accessAfter: user.accessAfter.map((tin) =>
      modifiedTins.some((mTin) => mTin.tin === tin.tin)
        ? {
            ...tin,
            entitlements: selectedEntitlements,
          }
        : tin,
    ),
  });

  preserveDisabledRolesForBulkEdit = (user: EditedUser): EditedUser => {
    const accessBefore = user.accessBefore;

    const addBackDisabledEntitlements = (tinAfter: TinAccess): TinAccess => {
      const entitlementsBefore = accessBefore
        .find((t) => t.tin === tinAfter.tin && t.entitlements)
        ?.entitlements?.filter(
          (e) =>
            e?.name === JOB_ROLE_DELEGATE_NAME ||
            e?.name === COLLABORATIVE_CARE_ENTITLEMENT,
        );
      return tinAfter.entitlements && entitlementsBefore?.length
        ? {
            ...tinAfter,
            entitlements: tinAfter.entitlements.concat(entitlementsBefore),
          }
        : tinAfter;
    };

    return {
      ...user,
      accessAfter: user.accessAfter.map(addBackDisabledEntitlements),
    };
  };
}
